"use client";

import type React from "react";
import { useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
  Text,
  HStack,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import type { Edge } from "./types";

interface EdgePropertiesDialogProps {
  edge: Edge;
  onClose: () => void;
  onUpdate: (edge: Edge) => void;
  onDelete: (edgeId: string) => void;
}

export const EdgePropertiesDialog: React.FC<EdgePropertiesDialogProps> = ({
  edge,
  onClose,
  onUpdate,
  onDelete,
}) => {
  // Default JSON schema scaffold
  const defaultParameters = useMemo(
    () => ({
      type: "object",
      properties: {} as Record<string, unknown>,
    }),
    []
  );

  const initialParameters = edge.data?.parameters || defaultParameters;

  const [label, setLabel] = useState(edge.label || "");
  const [parametersJson, setParametersJson] = useState(
    JSON.stringify(initialParameters, null, 2)
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleSave = () => {
    try {
      let parameters = defaultParameters;

      if (parametersJson?.trim()) {
        parameters = JSON.parse(parametersJson);
        // Ensure required schema shape
        if (!(parameters as any).type) (parameters as any).type = "object";
        if (!(parameters as any).properties)
          (parameters as any).properties = {};
      }

      setJsonError(null);

      const updatedEdge: Edge = {
        ...edge,
        label,
        data: {
          ...edge.data,
          parameters,
        },
      };

      onUpdate(updatedEdge);
      onClose();
    } catch {
      setJsonError("Invalid JSON format");
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(parametersJson);
      setParametersJson(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON format");
    }
  };

  const loadExample = (exampleNum: number) => {
    let exampleJson: any = {};

    if (exampleNum === 1) {
      exampleJson = {
        type: "object",
        properties: {
          customer_name: {
            type: "string",
            description: "The name of the customer",
          },
        },
        required: ["customer_name"],
      };
    } else if (exampleNum === 2) {
      exampleJson = {
        type: "object",
        properties: {
          appointment_date: {
            type: "string",
            description: "The date of the appointment",
          },
          appointment_time: {
            type: "string",
            description: "The time of the appointment",
          },
        },
        required: ["appointment_date", "appointment_time"],
      };
    } else if (exampleNum === 3) {
      exampleJson = {
        type: "object",
        properties: {
          issue_type: {
            type: "string",
            description: "The type of issue reported",
          },
          urgency: {
            type: "string",
            description: "How urgent the issue is",
            enum: ["low", "medium", "high"],
          },
        },
      };
    }

    setParametersJson(JSON.stringify(exampleJson, null, 2));
    setJsonError(null);
  };

  const isJsonValid = useMemo(() => {
    try {
      JSON.parse(parametersJson);
      return true;
    } catch {
      return false;
    }
  }, [parametersJson]);

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent
        bg="gray.100"
        color="text.primary"
        borderRadius="2xl"
        shadow="xl"
        borderWidth="1px"
        borderColor="border.subtle"
      >
        <ModalHeader color="text.emphasized">Agent Edge</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            {/* Path Condition */}
            <FormControl>
              <FormLabel color="secondary.600">Path Condition</FormLabel>
              <Text fontSize="xs" color="text.muted" mb={2}>
                Describe the condition that triggers the transition to the next
                state.
              </Text>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="The condition that triggers the transition to the next state"
                bg="bg.input"
                borderColor="border.default"
                _hover={{ borderColor: "border.emphasized" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                variant="outline"
                rounded="lg"
              />
            </FormControl>

            <Divider borderColor="border.subtle" />

            {/* Variables Schema */}
            <FormControl isInvalid={!!jsonError}>
              <HStack justify="space-between" align="center">
                <FormLabel m={0} color="secondary.600">
                  Store Call Info as Variables (Optional)
                </FormLabel>
                <Badge
                  variant="subtle"
                  colorScheme={isJsonValid ? "green" : "red"}
                  rounded="md"
                >
                  {isJsonValid ? "JSON OK" : "Invalid JSON"}
                </Badge>
              </HStack>

              <Text fontSize="xs" color="text.muted" mb={2}>
                Convert information from the previous node into variables for
                use in future nodes. Provide a JSON Schema describing the
                expected structure for the LLM output.
              </Text>

              <Textarea
                value={parametersJson}
                onChange={(e) => setParametersJson(e.target.value)}
                placeholder="Enter JSON Schema here..."
                minHeight="220px"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"
                bg="bg.input"
                borderColor="border.default"
                _hover={{ borderColor: "border.emphasized" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                rounded="lg"
                sx={{
                  "&::-webkit-scrollbar": { height: "6px", width: "8px" },
                  "&::-webkit-scrollbar-track": { background: "bg.subtle" },
                  "&::-webkit-scrollbar-thumb": {
                    background: "border.default",
                    borderRadius: "md",
                  },
                  scrollbarColor:
                    "var(--chakra-colors-border-default) var(--chakra-colors-bg-subtle)",
                }}
              />

              {jsonError && (
                <Text color="red.500" fontSize="sm" mt={2}>
                  {jsonError}
                </Text>
              )}

              <HStack mt={3} spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => loadExample(1)}
                >
                  Example 1
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => loadExample(2)}
                >
                  Example 2
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => loadExample(3)}
                >
                  Example 3
                </Button>
              </HStack>

              <Button
                mt={3}
                width="100%"
                variant="outline"
                colorScheme="gray"
                onClick={formatJson}
              >
                Format JSON
              </Button>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack w="full" justify="space-between">
            <Button
              variant="ghost"
              colorScheme="red"
              leftIcon={<Trash2 size={16} />}
              onClick={() => {
                onDelete(edge.id);
                onClose();
              }}
            >
              Delete
            </Button>

            <HStack>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="primary" onClick={handleSave}>
                Save
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
